import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  CircularProgress,
} from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useSelector } from "react-redux";

const RcListView = ({ onAddRc, onViewPo, savedRc }) => {
  const loginData = useSelector((state) => state.loginData.value);
  const auth_token = loginData?.auth_token;
  const buyer_token = loginData?.user?.token;

  const [rcList, setRcList] = useState([]);
  const [, setLoading] = useState(false);

  const [loadingPoId, setLoadingPoId] = useState(false);

  useEffect(() => {
    const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
    if (!chatActionRaw) return;

    const { action } = JSON.parse(chatActionRaw);

    // consume once
    sessionStorage.removeItem("CHAT_ACTION");

    if (action === "navigate_create_rate_contract") {
      onAddRc();
      return;
    }
  }, []);

  const fetchSavedRC = async () => {
    try {
      setLoading(true);

      const res = await axios.post(
        API_ENDPOINT.get_saved_rate_contracts,
        { buyer_token },
        {
          headers: {
            Authorization: `Token ${auth_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("FETCH RC:", res.data);
      setRcList(res.data || []);
    } catch (err) {
      console.error("FETCH RC ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedRC();
  }, []);

  useEffect(() => {
    if (savedRc) {
      setRcList((prev) => {
        const exists = prev.some((rc) => rc.rc_token === savedRc.rc_token);
        return exists ? prev : [savedRc, ...prev];
      });
    }
  }, [savedRc]);

  const handleCreatePO = async (rcItem) => {
    try {
      const token = rcItem.rc_token || rcItem.token;
      setLoadingPoId(token);
      setLoading(true);

      const res = await axios.post(
        API_ENDPOINT.get_rc_items_,
        { rc_token: token },
        {
          headers: {
            Authorization: `Token ${auth_token}`,
            "Content-Type": "application/json",
          },
        }
      );

      onViewPo({
        items: res.data,
        rc_token: token,
      });
    } catch (err) {
      console.error("GET RC ITEMS ERROR:", err);
    } finally {
      setLoading(false);
      setLoadingPoId(null); // reset only after loading
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Rate Contract" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={onAddRc}
        >
          Create RateContract
        </Button>
      </Box>

      {/* CARD VIEW */}
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
        }}
      >
        {rcList.map((item, index) => (
          <Card
            key={index}
            sx={{
              borderRadius: "12px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              border: "1px solid #e2e8f0",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 18px rgba(0,0,0,0.12)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontFamily: "GilroySemiBold" }}
              >
                Rate Contract No: {item.rc_number}
              </Typography>

              <Typography
                sx={{
                  fontSize: "14px",
                  fontFamily: "GilroyMedium",
                  mb: 0.5,
                }}
              >
                <strong>Start Date:</strong> {item.start_date}
              </Typography>

              <Typography
                sx={{
                  fontSize: "14px",
                  fontFamily: "GilroyMedium",
                  mb: 0.5,
                }}
              >
                <strong>End Date:</strong> {item.end_date || "—"}
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  fontWeight: 600,
                  color: "#075b97",
                  fontSize: "14px",
                  fontFamily: "GilroyMedium",
                }}
              >
                Total Contract Value: ₹
                {Number(item.contract_value).toLocaleString("en-IN")}
              </Typography>
            </CardContent>

            <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  fontSize: "12px",
                  opacity: loadingPoId === item.rc_token ? 0.6 : 1,
                }}
                // disabled={ loadingPoId === item.rc_token }
                onClick={() => handleCreatePO(item)}
              >
                {loadingPoId ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Create PO"
                )}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* TABLE VIEW */}
      <Box sx={{ mt: "20px", width: "100%", overflowX: "auto" }}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "RC Number",
                  "Buyer",
                  "Start Date",
                  "End Date",
                  "Total Contract Value",
                  "Action",
                ].map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: 600,
                      fontFamily: "GilroySemiBold",
                      backgroundColor: "#e0e9ee",
                      fontSize: "14px",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rcList.map((item, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: "white",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <TableCell
                    sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}
                  >
                    {item.rc_number}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}
                  >
                    {item.buyer}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}
                  >
                    {item.start_date}
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}
                  >
                    {item.end_date || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#075b97",
                      fontWeight: 600,
                      fontFamily: "GilroyMedium",
                      fontSize: "14px",
                    }}
                  >
                    ₹{Number(item.contract_value).toLocaleString("en-IN")}
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="outlined"
                      size="medium"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 1.5,
                        fontSize: "12px",
                        opacity: loadingPoId === item.rc_token ? 0.6 : 1,
                      }}
                      disabled={loadingPoId === item.rc_token}
                      onClick={() => handleCreatePO(item)}
                    >
                      {loadingPoId === item.rc_token ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        "Create PO"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default RcListView;
